import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { Spinner } from './Spinner.jsx';

/**
 * The one button. Every call to action in the store is this component with a
 * variant, which is what keeps focus rings, disabled states, and loading
 * behaviour identical everywhere.
 *
 * A button that is loading stays focusable and keeps its width, so the layout
 * does not jump and a keyboard user does not lose their place.
 */

const base =
  'inline-flex items-center justify-center gap-2 rounded ' +
  'transition-[background-color,border-color,color,opacity] duration-150 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 select-none';

const variants = {
  primary: 'bg-brand text-white hover:bg-brand-dark focus-visible:outline-brand',
  accent: 'bg-accent text-white hover:opacity-88 focus-visible:outline-accent',
  outline:
    'border border-brand bg-surface text-brand hover:bg-brand hover:text-white focus-visible:outline-brand',
  ghost: 'text-ink hover:bg-sand/60 focus-visible:outline-brand',
  quiet: 'text-muted hover:text-brand focus-visible:outline-brand',
  /**
   * For a button sitting on a saturated band — the sale banner — where the gold
   * button would disappear. It is a variant rather than a className override
   * because `cn` is plain clsx with no tailwind-merge: passing `bg-white
   * text-ink` alongside the primary variant left `bg-brand text-white` in the
   * class list too, and the winner came down to CSS source order. That produced
   * a white button with white text, which is exactly as readable as it sounds.
   */
  inverse: 'bg-white text-ink hover:bg-white/90 focus-visible:outline-white',
  danger: 'bg-danger text-white hover:bg-danger/88 focus-visible:outline-danger',
  link: 'text-ink underline decoration-mist underline-offset-4 hover:text-brand hover:decoration-brand focus-visible:outline-brand',
};

/**
 * The filled and bordered variants are set as small tracked capitals, the
 * house's call-to-action voice; the quiet ones stay in running-text case. The
 * type size lives here rather than in `sizes` for the same no-tailwind-merge
 * reason as above — two font-size classes on one element is a coin toss.
 */
const CAPS = new Set(['primary', 'accent', 'outline', 'inverse', 'danger']);

const type = {
  caps: {
    xs: 'text-2xs font-semibold tracking-[0.08em] uppercase',
    sm: 'text-2xs font-semibold tracking-[0.08em] uppercase',
    md: 'text-2xs font-semibold tracking-[0.08em] uppercase',
    lg: 'text-xs font-semibold tracking-[0.08em] uppercase',
    icon: '',
  },
  plain: {
    xs: 'text-2xs font-medium',
    sm: 'text-xs font-medium',
    md: 'text-sm font-medium',
    lg: 'text-sm font-medium',
    icon: '',
  },
};

const sizes = {
  xs: 'h-8 px-3',
  sm: 'h-9 px-3.5',
  md: 'h-11 px-5',
  lg: 'h-13 px-7',
  icon: 'h-10 w-10 p-0',
};

export const Button = forwardRef(function Button(
  {
    as,
    to,
    href,
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel = 'Working',
    fullWidth = false,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const classes = cn(
    base,
    variants[variant],
    sizes[size],
    type[CAPS.has(variant) ? 'caps' : 'plain'][size],
    fullWidth && 'w-full',
    className,
  );

  const content = (
    <>
      {loading && <Spinner className="size-4" />}
      <span className={cn(loading && 'sr-only')}>{loading ? loadingLabel : children}</span>
      {loading && <span aria-hidden="true">{loadingLabel}</span>}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...props}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...props}>
        {children}
      </a>
    );
  }

  const Component = as ?? 'button';

  return (
    <Component
      ref={ref}
      type={Component === 'button' ? (props.type ?? 'button') : undefined}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Component>
  );
});

export default Button;
